import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { ReactNode } from "react"

import { toast } from "sonner"

import type { SegmentAnalysisAction } from "../hooks/use-segment-analysis"
import type { SegmentAnalysisView } from "../types"
import type { CompanyDirectoryEntry } from "../utils/segment-connections"

import { AgentRunModal } from "./agent-run-modal"
import { CcpPlanView } from "./ccp/ccp-plan-view"
import { CompaniesIndex } from "./companies-index"
import { CompanyDossier } from "./company-dossier"
import { CompetitorPlanView } from "./competitor-plan/competitor-plan-view"
import { ConnectionsGraph } from "./connections-graph"
import { SegmentAnalysisBackendState } from "./segment-analysis-backend-state"
import { SegmentAnalysisEmptyDetail } from "./segment-analysis-empty-detail"
import { SegmentAnalysisHeader } from "./segment-analysis-header"
import { SegmentAnalysisPortfolio } from "./segment-analysis-portfolio"
import { SegmentDetail } from "./segment-detail"
import { SegmentWizard } from "./segment-wizard"
import { listSegmentCompanies } from "../api/company-analysis-api"
import { isSelectionToggleOff, useSegmentAnalysis } from "../hooks/use-segment-analysis"
import { useSegmentEditPermission } from "../hooks/use-segment-edit-permission"
import { useSegmentReportFor } from "../hooks/use-segment-report-for"
import { useSegmentViewPrefs } from "../hooks/use-segment-view-prefs"
import { APP_NAME } from "../utils/branding"
import { SEGMENT_DEMO_DATA } from "../utils/demo-mode"
import { matchCompanyByName, sortSegmentsForGrid } from "../utils/segment-analysis-utils"
import { buildCompanyDirectory } from "../utils/segment-connections"
import { companyRoleInSegment } from "../utils/segment-drillin"
import { companiesCsv, downloadCsv, segmentsCsv } from "../utils/segment-export"
import { applyAddressableLinePrefs, effectiveCharts } from "../utils/segment-view-prefs"

type PlanView = {
  kind: "ccp" | "competitorPlan"
  id: string
  name: string
  fromSegmentId: string
  /** Prospects: auto-generate CCP on first open when no plan exists. */
  autoDraft?: boolean
}

export function SegmentAnalysisModule() {
  // Declared before the hook call: the hook needs the active view to resolve
  // the detail-pane selection against the right scope (#3311 review finding 3).
  const [view, setView] = useState<SegmentAnalysisView>("portfolio")
  const model = useSegmentAnalysis(view)
  const [company, setCompany] = useState<{ id: string; fromSegmentId: string } | null>(null)
  // CCP / Competitor Plan overlay — separate from the company dossier so the
  // Customers/Competitors CTAs and Map drill-ins route to plans while the Companies
  // index (and the dossier's own Related row) keep dossiers.
  const [planView, setPlanView] = useState<PlanView | null>(null)
  const detailRef = useRef<HTMLDivElement>(null)
  const [scrollToken, setScrollToken] = useState(0)
  // Monotonic token to ignore stale in-flight drill-down resolves: a subject change
  // (segment select / mode toggle) or a newer drill-in bumps it, so an earlier
  // listSegmentCompanies promise that resolves late can't re-open a closed dossier.
  const drillTokenRef = useRef(0)

  // User layer (§G9): per-user view prefs, layered client-side over the tenant-merged
  // config the backend returns. Available to every role; roams via the backend with
  // a localStorage paint cache (#2070).
  const [viewPrefs, setViewPrefs, viewPrefsReady] = useSegmentViewPrefs(model.currentUserOid)
  // The user can hide tenant addressable lines (never invent new ones) — apply that here.
  const effectiveAddressableLines = useMemo(
    () => applyAddressableLinePrefs(model.addressableLines, viewPrefs),
    [model.addressableLines, viewPrefs]
  )
  // Which portfolio graphs the user picked (#2068 B1, default bubble) — stacked in order.
  const charts = effectiveCharts(viewPrefs)
  // Apply the persisted default landing view + sticky filters once per resolved
  // identity (live changes flow through the customizer/reducer; this only handles
  // the on-load restore). #2068 C2: filters are restored from the same prefs blob.
  // Refs keep effect deps narrow: only real triggers re-run them, not every
  // prefs write-back.
  const viewPrefsRef = useRef(viewPrefs)
  viewPrefsRef.current = viewPrefs
  const setViewPrefsRef = useRef(setViewPrefs)
  setViewPrefsRef.current = setViewPrefs
  const defaultViewAppliedFor = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    // Gate on the SETTLED backend prefs (#2070): firing on mount would apply the
    // empty/default prefs and never re-fire once the real ones arrive.
    if (!viewPrefsReady) return
    if (defaultViewAppliedFor.current === model.currentUserOid) return
    defaultViewAppliedFor.current = model.currentUserOid
    const persisted = viewPrefsRef.current
    if (persisted.defaultView) setView(persisted.defaultView)
    if (persisted.stickyFilters) {
      model.dispatch({ type: "setFilters", filters: persisted.stickyFilters })
    }
  }, [viewPrefsReady, model.currentUserOid, model.dispatch])

  // Persist the active filters as the sticky set (#2068 C2). Ref-based so only a
  // real filter change writes — the write-back of viewPrefs can't loop the effect.
  const filtersHydrated = useRef(false)
  useEffect(() => {
    // Never write before the backend prefs settle (#2070): a reducer-driven
    // filter identity change during load could otherwise PUT the empty cache
    // over the user's roaming prefs.
    if (!viewPrefsReady) return
    // Skip the very first run (initial empty filters would clobber a sticky set
    // before the hydrate effect above has dispatched it).
    if (!filtersHydrated.current) {
      filtersHydrated.current = true
      return
    }
    const filters = model.state.filters
    // Priority removed from SegmentFilters entirely (#3295 Nolato feedback #19,
    // following the #2421 UI removal) — nothing left to strip here.
    const stickyFilters: typeof filters = { ...filters }
    const hasAny =
      stickyFilters.market.length > 0 ||
      stickyFilters.owner.length > 0 ||
      stickyFilters.search !== "" ||
      stickyFilters.favouritesOnly
    setViewPrefsRef.current({
      ...viewPrefsRef.current,
      stickyFilters: hasAny ? stickyFilters : undefined,
    })
  }, [model.state.filters, viewPrefsReady])

  // Bring the detail/dossier into view only on a deliberate drill-in — a company
  // open or a segment picked from the Companies/Map views, where it mounts below
  // the fold. Deliberately NOT on filter changes, clear-selection, or portfolio
  // card clicks, which would yank the page around. Drill-ins bump scrollToken.
  useEffect(() => {
    if (scrollToken === 0) return
    // Defer so the freshly-mounted detail lays out first. Use an instant scroll —
    // a smooth one gets interrupted by the dossier's async data render.
    const timer = window.setTimeout(() => {
      detailRef.current?.scrollIntoView({ block: "start" })
    }, 120)
    return () => window.clearTimeout(timer)
  }, [scrollToken])

  // Bridge the reducer's transient toastMessage to the app-wide sonner toaster.
  // Several reducer actions (segment created, drill-in miss/error) set this field,
  // but nothing renders it on its own — surface them, then clear so it fires once.
  useEffect(() => {
    if (!model.state.toastMessage) return
    toast(model.state.toastMessage)
    model.dispatch({ type: "clearToast" })
  }, [model.state.toastMessage, model.dispatch])

  // Wrap the reducer dispatch so any deliberate change of the detail subject also
  // closes an open company dossier (which has render precedence over the segment
  // detail). Done ON THE ACTION — not by detecting a selectedSegmentId change — so
  // it fires even when re-selecting the already-selected segment, the case a
  // change-detection effect misses when the dossier was opened over a DIFFERENT
  // segment than the one selected underneath. Also invalidates any in-flight
  // drill-down so a late resolve can't re-open the dossier. Opening a company never
  // dispatches these actions, so a fresh dossier is never clobbered.
  const dispatch = useCallback(
    (action: SegmentAnalysisAction) => {
      // Hero-card toggle (§ #2063): re-clicking the already-selected card is the
      // "clear" gesture — two-step when a dossier is open (first click returns to
      // the segment detail, the next one clears the selection entirely). Compared
      // against the resolved selection, so it also works after a filter fallback.
      if (isSelectionToggleOff(action, model.selectedSegment)) {
        drillTokenRef.current += 1
        if (planView) {
          setPlanView(null)
          return
        }
        if (company) {
          setCompany(null)
          return
        }
        model.dispatch({ type: "clearSelection" })
        return
      }
      if (
        action.type === "selectSegment" ||
        action.type === "clearSelection" ||
        action.type === "setUseMockData"
      ) {
        drillTokenRef.current += 1
        setCompany(null)
        setPlanView(null)
      }
      model.dispatch(action)
      // #2068 D1: a fresh segment selection lands on the user's preferred tab.
      // Dispatched in the same tick, so the reducer's "market" reset never paints.
      const preferredTab = viewPrefsRef.current.defaultDetailTab
      if (action.type === "selectSegment" && preferredTab) {
        model.dispatch({ type: "setDetailTab", tab: preferredTab })
      }
    },
    [model.dispatch, model.selectedSegment, company, planView]
  )

  // Drill-ins resolve permission from THEIR segment, never the grid selection.
  const canEditSegment = useSegmentEditPermission(model)

  // Companies index + connections graph derive entirely from the (filtered) portfolio,
  // so the active filters narrow both — no extra fetch needed.
  const directory = useMemo(
    () => buildCompanyDirectory(model.marketOwnerFilteredSegments),
    [model.marketOwnerFilteredSegments]
  )

  // Resolve the company's canonical id via the registry (registers + links it),
  // so the drill-down uses the backend id rather than a drift-prone client slug.
  // Dossiers are backed by the live API. Only the EXPLICIT mock sandbox can't open
  // them by design (the registry has no matching record → 404), so point there to
  // the real recovery — switch to Backend live. In backend mode, or a transparent
  // mock-fallback after a backend load failure, a miss/404 is a genuine backend gap,
  // so surface a retryable hint instead of the sandbox message.
  async function handleAnalyzeCompany(name: string, segmentId: string) {
    const inSandbox = model.dataSource === "mock"
    const sandboxHint = "Company dossiers open in Backend live mode."
    // Claim a token for this drill-down; if the subject changes (or a newer drill-in
    // starts) before the request resolves, every post-await branch below bails out so
    // a stale resolve can neither re-open a closed dossier nor fire a late toast.
    const requestToken = (drillTokenRef.current += 1)
    const isStale = () => drillTokenRef.current !== requestToken
    try {
      const { companies } = await listSegmentCompanies(segmentId)
      if (isStale()) return
      const match = matchCompanyByName(companies, name)
      if (match) {
        setCompany({ id: match.id, fromSegmentId: segmentId })
        setScrollToken((token) => token + 1)
      } else {
        model.dispatch({
          type: "showToast",
          message: inSandbox ? sandboxHint : `No dossier available for ${name} yet.`,
        })
      }
    } catch {
      if (isStale()) return
      // Drill-down is best-effort; surface a hint rather than failing silently.
      model.dispatch({
        type: "showToast",
        message: inSandbox ? sandboxHint : `Couldn't open ${name} — please try again.`,
      })
    }
  }

  // Open a CCP / Competitor Plan for a named company. Clones the dossier drill-down
  // resolution (name → canonical id via the registry) but targets the plan overlay
  // instead of the CompanyDossier. Same mock-sandbox gate: the registry has no record
  // in the explicit mock sandbox, so point the user at Backend live mode.
  async function openPlan(input: {
    kind: PlanView["kind"]
    name: string
    segmentId: string
    autoDraft?: boolean
  }) {
    const { kind, name, segmentId, autoDraft } = input
    const inSandbox = model.dataSource === "mock"
    const requestToken = (drillTokenRef.current += 1)
    const isStale = () => drillTokenRef.current !== requestToken
    try {
      const { companies } = await listSegmentCompanies(segmentId)
      if (isStale()) return
      const match = matchCompanyByName(companies, name)
      if (match) {
        setCompany(null)
        setPlanView({
          kind,
          id: match.id,
          name: match.name,
          fromSegmentId: segmentId,
          autoDraft,
        })
        setScrollToken((token) => token + 1)
      } else {
        model.dispatch({
          type: "showToast",
          message: inSandbox
            ? "Plans open in Backend live mode."
            : `No plan available for ${name} yet.`,
        })
      }
    } catch {
      if (isStale()) return
      model.dispatch({
        type: "showToast",
        message: inSandbox
          ? "Plans open in Backend live mode."
          : `Couldn't open a plan for ${name} — please try again.`,
      })
    }
  }

  const handleOpenCcp = (name: string, segmentId: string, options?: { autoDraft?: boolean }) =>
    openPlan({ kind: "ccp", name, segmentId, autoDraft: options?.autoDraft })
  const handleOpenCompetitorPlan = (name: string, segmentId: string) =>
    openPlan({ kind: "competitorPlan", name, segmentId })
  // Map + related-chip drill-ins (Nolato #13) open the plan for the company's role in
  // THAT segment — exactly what the Customers/Competitors tabs open. The Companies
  // tab keeps the dossier; an unlisted company falls back to it too.
  const handleOpenCompanyPlan = (name: string, segmentId: string) => {
    const segment = model.allSegments.find((candidate) => candidate.id === segmentId)
    const role = companyRoleInSegment(segment, name)
    if (role === "customer") return handleOpenCcp(name, segmentId)
    if (role === "competitor") return handleOpenCompetitorPlan(name, segmentId)
    return handleAnalyzeCompany(name, segmentId)
  }
  const planSegmentReport = useSegmentReportFor(model, planView?.fromSegmentId)

  // Key the swap container to the active subject so the enter animation re-fires
  // only when the context changes (segment → company → empty), not on data refresh.
  // Precedence mirrors the render order below: company > segment > empty.
  let detailKey = "empty"
  if (planView) detailKey = `plan:${planView.kind}:${planView.id}`
  else if (company) detailKey = `company:${company.id}`
  else if (model.selectedSegment) detailKey = `segment:${model.selectedSegment.id}`

  // CCP / Competitor Plan overlay content — precedence over dossier + segment detail.
  let planContent: ReactNode = null
  if (planView) {
    // Everything is resolved for the PLAN's segment (permission, name, evidence) —
    // never the grid selection, which a Map drill-in doesn't change.
    const planProps = {
      canEdit: canEditSegment(planView.fromSegmentId),
      companyId: planView.id,
      companyName: planView.name,
      directory,
      segmentId: planView.fromSegmentId,
      segmentName:
        model.allSegments.find((segment) => segment.id === planView.fromSegmentId)?.name ?? "",
      segmentReport: planSegmentReport,
      onBack: () => {
        drillTokenRef.current += 1
        setPlanView(null)
      },
      onOpenCompany: handleOpenCompanyPlan,
    }
    planContent =
      planView.kind === "ccp" ? (
        <CcpPlanView {...planProps} autoDraft={Boolean(planView.autoDraft)} />
      ) : (
        <CompetitorPlanView {...planProps} />
      )
  }

  // Export what the user is currently looking at: the company directory in the
  // Companies view, otherwise the segment set that view is actually rendering.
  // Map has no control for the Portfolio-only search/Favourites filter, so it
  // renders `marketOwnerFilteredSegments` (#3306 #7) — export must follow suit
  // or it silently drops segments the map still shows (#3311 review finding 2).
  const handleExport = () => {
    const filePrefix = APP_NAME.toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
    if (view === "companies") {
      downloadCsv(`${filePrefix}-companies.csv`, companiesCsv(directory))
      toast.success(`Exported ${directory.length} companies to CSV.`)
    } else {
      const exportSegments =
        view === "map" ? model.marketOwnerFilteredSegments : model.visibleSegments
      downloadCsv(`${filePrefix}-segments.csv`, segmentsCsv(exportSegments))
      toast.success(`Exported ${exportSegments.length} segments to CSV.`)
    }
  }

  // Per-user favourites across the visible segments (#2072): the directory star
  // is lit when the company is favourited in ANY segment; toggling clears it
  // everywhere it is favourited, or sets it on the company's primary segment.
  const favouritedCompanyNames = useMemo(() => {
    const names = new Set<string>()
    if (model.currentUserOid) {
      for (const segment of model.marketOwnerFilteredSegments) {
        for (const name of segment.favouriteCompaniesBy?.[model.currentUserOid] ?? []) {
          names.add(name)
        }
      }
    }
    return names
  }, [model.marketOwnerFilteredSegments, model.currentUserOid])

  const handleToggleDirectoryFavourite = (entry: CompanyDirectoryEntry) => {
    const oid = model.currentUserOid
    const segmentsById = new Map(
      model.marketOwnerFilteredSegments.map((segment) => [segment.id, segment])
    )
    const favouritedIn = entry.segments.filter((ref) =>
      oid
        ? (segmentsById.get(ref.id)?.favouriteCompaniesBy?.[oid] ?? []).includes(entry.name)
        : false
    )
    if (favouritedIn.length > 0) {
      for (const ref of favouritedIn) void model.toggleCompanyFavourite(entry.name, ref.id)
    } else {
      void model.toggleCompanyFavourite(entry.name, entry.segments[0].id)
    }
  }

  // Filter presets (#2069 C1): save the active combo under a name, recall or
  // delete via chips in the filter bar. Persisted in the same §G9 prefs blob.
  const filterPresets = viewPrefs.filterPresets ?? []
  const saveFilterPreset = (name: string) => {
    const trimmed = name.trim().slice(0, 40)
    if (!trimmed) return
    const next = [
      ...filterPresets.filter((preset) => preset.name !== trimmed),
      { name: trimmed, filters: model.state.filters },
    ].slice(-8)
    setViewPrefs({ ...viewPrefs, filterPresets: next })
  }
  const deleteFilterPreset = (name: string) => {
    setViewPrefs({
      ...viewPrefs,
      filterPresets: filterPresets.filter((preset) => preset.name !== name),
    })
  }
  const applyFilterPreset = (name: string) => {
    const preset = filterPresets.find((entry) => entry.name === name)
    if (preset) dispatch({ type: "setFilters", filters: preset.filters })
  }
  let viewSlot: ReactNode = null
  if (view === "companies") {
    viewSlot = (
      <CompaniesIndex
        directory={directory}
        // prettier-ignore
        headerFiltersActive={model.allSegments.length > 0 && (model.state.filters.market.length > 0 || model.state.filters.owner.length > 0)}
        onOpenCompany={handleAnalyzeCompany}
        {...(model.canFavouriteCompany
          ? {
              isFavourite: (name: string) => favouritedCompanyNames.has(name),
              onToggleFavourite: handleToggleDirectoryFavourite,
            }
          : {})}
      />
    )
  } else if (view === "map") {
    viewSlot = (
      <ConnectionsGraph
        segments={model.marketOwnerFilteredSegments}
        selectedSegmentId={model.selectedSegment?.id ?? null}
        onOpenCompany={handleOpenCompanyPlan}
        onSelectSegment={(id) => {
          // A node click is a deliberate drill-in from the map; the detail mounts
          // below the fold, so bring it into view (same as a company drill-in).
          dispatch({ type: "selectSegment", segmentId: id })
          setScrollToken((token) => token + 1)
        }}
      />
    )
  }

  // Live-data-only tenants (no demo dataset opt-in) gate the module on the
  // backend query instead of silently painting the demo portfolio (#2768).
  if (!SEGMENT_DEMO_DATA && model.backendStatus !== "ready") {
    return (
      <SegmentAnalysisBackendState
        backendError={model.backendError}
        backendStatus={model.backendStatus}
      />
    )
  }

  return (
    <main className="bg-background text-foreground min-h-full" data-testid="segment-analysis-page">
      <div className="module-enter">
        <SegmentAnalysisPortfolio
          addressableLines={effectiveAddressableLines}
          allSegments={model.allSegments}
          canCreate={model.canCreateSegment}
          cardStats={viewPrefs.cardStats}
          charts={charts}
          currentUserOid={model.currentUserOid}
          dispatch={dispatch}
          filterPresets={filterPresets.map((preset) => preset.name)}
          kpiCardIds={viewPrefs.kpiCards}
          kpiDensity={viewPrefs.kpiDensity}
          marketOwnerFilteredSegments={model.marketOwnerFilteredSegments}
          marketOwnerSummary={model.marketOwnerSummary}
          owners={model.owners}
          pendingCounts={model.pendingCounts}
          role={model.effectiveRole}
          selectedSegmentId={model.selectedSegment?.id ?? null}
          showFavourites={model.dataSource === "backend"}
          staleDays={viewPrefs.staleDays}
          state={model.state}
          summary={model.summary}
          view={view}
          viewSlot={viewSlot}
          headerSlot={
            <SegmentAnalysisHeader
              allSegments={model.allSegments}
              backendError={model.backendError}
              backendGeneratedAt={model.backendGeneratedAt}
              backendStatus={model.backendStatus}
              dataSource={model.dataSource}
              dispatch={dispatch}
              effectiveRole={model.effectiveRole}
              owners={model.owners}
              state={model.state}
            />
          }
          visibleSegments={sortSegmentsForGrid(
            model.visibleSegments,
            viewPrefs.gridSort ?? "own-first"
          )}
          onApplyFilterPreset={applyFilterPreset}
          onDeleteFilterPreset={deleteFilterPreset}
          onExport={handleExport}
          onSaveFilterPreset={saveFilterPreset}
          onToggleFavourite={model.toggleFavourite}
          onViewChange={setView}
        />
        <div ref={detailRef} className="mx-auto max-w-[1480px] scroll-mt-4 px-4 pb-8 sm:px-6">
          <div key={detailKey} className="animate-in fade-in-0 slide-in-from-bottom-1 duration-200">
            {planContent}
            {!planView && company ? (
              <CompanyDossier
                canDecide={canEditSegment(company.fromSegmentId)}
                canRun={canEditSegment(company.fromSegmentId)}
                companyId={company.id}
                directory={directory}
                fromSegmentId={company.fromSegmentId}
                hiddenSections={viewPrefs.hiddenDossierSections}
                fromSegmentName={
                  model.allSegments.find((segment) => segment.id === company.fromSegmentId)?.name
                }
                onOpenCompany={handleAnalyzeCompany}
                onBack={() => {
                  // Bump the drill token so an in-flight related-company drill-down
                  // can't re-open the dossier after Back is clicked (ledger #30).
                  drillTokenRef.current += 1
                  setCompany(null)
                }}
              />
            ) : null}
            {!planView && !company && model.selectedSegment ? (
              <SegmentDetail
                addressableLines={effectiveAddressableLines}
                canEditMetrics={model.canEditManualMetrics}
                canFavouriteCompany={model.canFavouriteCompany}
                canReassignOwner={model.canReassignOwner}
                canRun={model.canEditSelectedSegment}
                claimRegistry={model.claimRegistry}
                currentUserOid={model.currentUserOid}
                dataSource={model.dataSource}
                dispatch={dispatch}
                favouriteCompanies={model.favouriteCompanies}
                hiddenDetailTabs={viewPrefs.hiddenDetailTabs}
                pendingChanges={model.pendingChanges}
                report={model.report}
                role={model.effectiveRole}
                segment={model.selectedSegment}
                state={model.state}
                onAnalyzeCompany={(name) => handleAnalyzeCompany(name, model.selectedSegment!.id)}
                onDecide={model.decidePendingChange}
                onReassignOwner={model.reassignOwner}
                onReclassifyCompany={model.reclassifyCompany}
                onStartRun={model.startRun}
                onToggleCompanyFavourite={model.toggleCompanyFavourite}
                onUpdateManualMetrics={model.updateManualMetrics}
                onOpenCcp={(name, options) =>
                  handleOpenCcp(name, model.selectedSegment!.id, options)
                }
                onOpenCompetitorPlan={(name) =>
                  handleOpenCompetitorPlan(name, model.selectedSegment!.id)
                }
              />
            ) : null}
            {/* Suppressed when there are no segments at all — the portfolio
                already shows the first-run empty state with the create CTA. */}
            {!planView && !company && !model.selectedSegment && model.allSegments.length > 0 ? (
              <SegmentAnalysisEmptyDetail
                canCreate={model.canCreateSegment}
                onCreate={() => dispatch({ type: "openWizard" })}
              />
            ) : null}
          </div>
        </div>
      </div>
      {model.state.isWizardOpen && (
        <SegmentWizard
          dispatch={dispatch}
          state={model.state}
          onCreate={async (draft) => {
            // createSegment auto-selects the new segment in the reducer; close any
            // open dossier — and invalidate any in-flight drill-down so a late resolve
            // can't reopen it — so the new segment's detail shows, not a stale dossier.
            drillTokenRef.current += 1
            setCompany(null)
            setPlanView(null)
            return model.createSegment(draft)
          }}
        />
      )}
      <AgentRunModal
        currentRun={model.currentRun}
        dataSource={model.dataSource}
        dispatch={dispatch}
        runModal={model.state.runModal}
        onConfirmRun={model.confirmRun}
      />
    </main>
  )
}
